import { Controller, useForm } from 'react-hook-form';
import { Button } from 'src/components/ui/button';
import React, { useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import {
  booleanScheme,
  momentScheme,
  numberScheme,
  passwordScheme,
  stringScheme,
} from 'src/utils/validation-schemes';
import {
  useCreateKnowledgeBaseCollectorMutation,
  useGetKnowledgeBaseCollectorsQuery,
} from 'src/services/knowledgeBaseService';
import { useParams } from 'react-router-dom';
import { useNotification } from 'src/hooks/useNotification';
import { useLoader } from 'src/provider/LoaderProvider';
import { MaskedDateInput } from 'src/components/MaskedDateInput/MaskedDateInput';
import moment from 'moment';
import { ISO_DATE_FORMAT } from 'src/shared/constants';
import Input from 'src/components/Input/Input';
import { Switch } from 'src/components/ui/switch';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import {
  WEBSITE_INTERVAL,
  TICKETS_INTERVAL,
  FRESHDESK_TICKETS_INITIAL_REQUEST,
} from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/CurrentKnowledgeBaseConstants';
import { generateUniqueName, INTEGRATION_NAMES_MAP } from 'src/utils/fileUtils';
import { InputInfoToolTip } from 'src/components/Input/InputInfoToolTip';
import { useAppSelector } from 'src/store';
import { FRESHDESK_URL } from 'src/constants/urls';
import { getSubdomainHelper } from 'src/helpers/getSubdomainHelper';
import { splitString } from 'src/utils/splitString';

export const FreshdeskTicketsForm = ({
  freshdeskIntegrationType,
  prevStep = () => {},
  closePopup = (..._args: any[]) => {},
  backButtonVisible = true,
  knowledgeBaseId = null,
  initialRequest = {},
}: {
  freshdeskIntegrationType?: any;
  prevStep?: () => void;
  closePopup?: (...args: any[]) => void;
  backButtonVisible?: boolean;
  knowledgeBaseId?: any;
  initialRequest?: any;
}) => {
  const { id } = useParams();
  const notification = useNotification();
  const loader = useLoader();
  const [
    createCollector,
    {
      data: createCollectorData,
      isLoading: isLoadingCreateCollector,
      isError: isErrorCreateCollector,
      isSuccess: isSuccessCreateCollector,
    },
  ] = useCreateKnowledgeBaseCollectorMutation() as any;
  const { data: collectorsResponse, isLoading: isLoadingCollectors } =
    useGetKnowledgeBaseCollectorsQuery(
      {
        id: knowledgeBaseId || id,
        include_data: false,
      },
      {
        skip: !(knowledgeBaseId || id),
      },
    );
  const { isTrialPlan } = useAppSelector((s) => s.planSlice);

  let type = null;

  switch (freshdeskIntegrationType) {
    case 'Help Center':
      type = 'freshdesk_helpcenter';
      break;
    case 'Tickets':
      type = 'freshdesk_tickets';
      break;
  }

  useEffect(() => {
    if (isSuccessCreateCollector) {
      notification.success(`${createCollectorData.name} Added Successfully.`);
      close(true);
    }
  }, [isSuccessCreateCollector]);

  useEffect(() => {
    if (isLoadingCreateCollector || isLoadingCollectors) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingCreateCollector, isLoadingCollectors]);

  useEffect(() => {
    if (isErrorCreateCollector) {
      close(true);
    }
  }, [isErrorCreateCollector]);

  const prev = () => {
    prevStep();
  };

  const close = (shouldCloseSourceModal = false) => {
    closePopup(shouldCloseSourceModal);
  };

  const {
    handleSubmit,
    control,
    watch,
    formState: { isDirty, isValid },
  } = useForm<any>({
    defaultValues: {
      start_from: '',
      agents: '',
      tags: '',
      groups: '',
      type: type,
      autoSync: !isTrialPlan,
      interval: WEBSITE_INTERVAL.MONTHLY,
      subdomain: '',
      api_key: '',
    },
    resolver: yupResolver(
      object({
        subdomain: stringScheme({ required: true }).label('Freshdesk API Subdomain'),
        api_key: passwordScheme({ required: true, min: 3, max: 100 }).label('Freshdesk API Key'),
        start_from: momentScheme({ required: true, valid: true }),
        agents: stringScheme({ required: false }),
        tags: stringScheme({ required: false }),
        groups: stringScheme({ required: false }),
        autoSync: booleanScheme({ required: false }).label('AutoSync'),
        interval: numberScheme({ required: false }).label('Interval'),
      }),
    ),
  });

  const submit = (data) => {
    if (isValid) {
      const body: any = { ...FRESHDESK_TICKETS_INITIAL_REQUEST, ...initialRequest };
      const name = `${INTEGRATION_NAMES_MAP[data.type]} (${data.subdomain})`;

      body.start_from = moment(data.start_from).startOf('day').format(ISO_DATE_FORMAT);
      body.agents = splitString(data.agents);
      body.tags = splitString(data.tags);
      body.groups = splitString(data.groups);
      body.schedule.enabled = data.autoSync;
      body.schedule.interval = data.interval;
      body.name = generateUniqueName(name, collectorsResponse);
      body.subdomain = data.subdomain;
      body.api_key = data.api_key;

      createCollector({ id: knowledgeBaseId || id, body });
    }
  };

  const isAutoSync = watch('autoSync');

  return (
    <form
      onSubmit={handleSubmit(submit)}
      id="freshdesk-integration-form-2"
      className={'h-full flex flex-col justify-between'}
    >
      <div className="flex flex-col gap-5 max-h-[510px] overflow-auto px-4">
        <div className={'flex flex-col gap-2.5'}>
          <div className={'flex flex-col gap-2.5'}>
            <p className={'text-sm text-ebony_opacity_80 font-semibold'}>Sync Tickets</p>
          </div>
          <div className={'flex flex-col gap-2.5'}>
            <div className={'flex flex-col gap-[5px]'}>
              <Controller
                control={control}
                name="start_from"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <MaskedDateInput
                    type={'text'}
                    label={'Tickets solved from*:'}
                    infoTooltip={{
                      side: 'right',
                      description: 'Sync all solved tickets from the set date till today',
                    }}
                    onChange={onChange}
                    value={value}
                    error={error}
                  />
                )}
              />
            </div>
            <div className={'flex flex-col gap-[5px]'}>
              <Controller
                control={control}
                name="agents"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type={'textarea'}
                    label={'Tickets solved by:'}
                    onChange={onChange}
                    value={value}
                    isValid={!error?.message}
                    invalidMessage={error?.message}
                    textareaRows={2}
                    className={'!text-sm'}
                    placeholder={'agent ID, agent ID, agent ID'}
                    infoTooltip={{
                      side: 'right',
                      description: 'Use agent IDs to ensure precise matching.',
                    }}
                  />
                )}
              />
            </div>
            <div className={'flex flex-col gap-[5px]'}>
              <Controller
                control={control}
                name="tags"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type={'textarea'}
                    label={'Tickets tagged with:'}
                    onChange={onChange}
                    value={value}
                    isValid={!error?.message}
                    invalidMessage={error?.message}
                    textareaRows={2}
                    className={'!text-sm'}
                    placeholder={'tag ID, tag ID, tag ID'}
                    infoTooltip={{
                      side: 'right',
                      description: 'Enter tag IDs to filter tickets accurately.',
                    }}
                  />
                )}
              />
            </div>
            <div className={'flex flex-col gap-[5px]'}>
              <Controller
                control={control}
                name="groups"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type={'textarea'}
                    label={'Tickets in group:'}
                    onChange={onChange}
                    value={value}
                    isValid={!error?.message}
                    invalidMessage={error?.message}
                    textareaRows={2}
                    className={'!text-sm'}
                    placeholder={'group ID, group ID, group ID'}
                    infoTooltip={{
                      side: 'right',
                      description: 'Use group IDs to avoid ambiguity.',
                    }}
                  />
                )}
              />
            </div>
            <div className={'flex flex-row gap-5 mt-2.5'}>
              <div>
                <Controller
                  control={control}
                  name="autoSync"
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <span className="flex flex-row gap-4">
                      <span className="flex items-center gap-4">
                        <Switch
                          type="black"
                          checked={value}
                          onCheckedChange={onChange}
                          disabled={isTrialPlan}
                        />
                      </span>
                      <span
                        className={
                          'text-ebony_opacity_30 font-medium font-jamjuree text-sm flex flex-row items-center gap-2'
                        }
                      >
                        Auto-sync
                        {isTrialPlan && (
                          <InputInfoToolTip
                            infoToolTip={{
                              side: 'right',
                              description: <>Unavailable for trial plan</>,
                            }}
                          />
                        )}
                      </span>
                    </span>
                  )}
                />
              </div>
              {isAutoSync && (
                <div>
                  <Controller
                    control={control}
                    name="interval"
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <RadioGroup
                        onChange={onChange}
                        onValueChange={onChange}
                        defaultValue={value}
                        disabled={isTrialPlan}
                        className="flex flex-row gap-5 items-center"
                      >
                        <div className="flex items-center space-x-[7px] space-y-0">
                          <RadioGroupItem
                            value={TICKETS_INTERVAL.WEEKLY as any}
                            id={'interval-weekly'}
                          />
                          <label htmlFor={'interval-weekly'} className="cursor-pointer">
                            <p className="font-medium text-sm text-ebony opacity-50 hover:opacity-100 transition">
                              Weekly
                            </p>
                          </label>
                        </div>
                        <div className="flex items-center space-x-[7px] space-y-0">
                          <RadioGroupItem
                            value={TICKETS_INTERVAL.MONTHLY as any}
                            id={'interval-monthly'}
                          />
                          <label htmlFor={'interval-monthly'} className="cursor-pointer">
                            <p className="font-medium text-sm text-ebony opacity-50 hover:opacity-100 transition">
                              Monthly
                            </p>
                          </label>
                        </div>
                      </RadioGroup>
                    )}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={'flex flex-col'}>
          <div className={'flex flex-col gap-2.5'}>
            <p className={'text-sm text-ebony_opacity_80 font-semibold'}>API Details</p>
          </div>
          <div className={'flex flex-col gap-2.5'}>
            <div className={'flex flex-col gap-[5px] mt-2.5'}>
              <Controller
                control={control}
                name="subdomain"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type={'text'}
                    label={'Freshdesk subdomain*'}
                    placeholder={'domain.com'}
                    className={'!py-2.5 !px-3.5'}
                    invalidMessage={error?.message}
                    value={value}
                    isValid={!error?.message}
                    onChange={(e) => {
                      const res = getSubdomainHelper(e.target.value);

                      if (res?.subdomain) {
                        onChange(res?.subdomain);
                      } else {
                        onChange(e.target.value);
                      }
                    }}
                    infoTooltip={{
                      side: 'right',
                      description: 'Link to your Freshdesk workspace.',
                    }}
                  />
                )}
              />
            </div>
            <div className={'flex flex-col gap-[5px]'}>
              <Controller
                control={control}
                name="api_key"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type={'password'}
                    label={'Freshdesk API Key*'}
                    className={'!py-2.5 !px-3.5'}
                    placeholder={'------------------------------------------------------'}
                    invalidMessage={error?.message}
                    value={value}
                    isValid={!error?.message}
                    onChange={onChange}
                    infoTooltip={{
                      side: 'right',
                      description: (
                        <>
                          Check how to generate the API Key{' '}
                          <a
                            href={FRESHDESK_URL.HOW_TO_FIND_YOUR_API_KEY}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline cursor-pointer"
                          >
                            here →
                          </a>
                        </>
                      ),
                    }}
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center mt-5">
        <div className="flex gap-2.5">
          <Button variant="outline" type="button" className="min-w-24" onClick={() => close()}>
            Cancel
          </Button>
          {backButtonVisible && (
            <Button variant="outline-light" type="button" className="min-w-24" onClick={prev}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="9"
                height="10"
                viewBox="0 0 9 10"
                fill="none"
                className="mr-1.5"
              >
                <path
                  d="M9 4.4375L2.15438 4.4375L5.29875 1.29312L4.5 0.499999L0 5L4.5 9.5L5.29313 8.70687L2.15438 5.5625L9 5.5625V4.4375Z"
                  fill="#05050D"
                />
              </svg>
              <span>Back</span>
            </Button>
          )}
          <Button type="submit" className="min-w-24">
            <span>Integrate</span>
          </Button>
        </div>
      </div>
    </form>
  );
};
